import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Grid } from '@mui/material';
import { IntegrationCard } from './components/IntegrationCard';
import { IntegrationModal } from './components/IntegrationModal';
import { integrations } from './data/integrations';
import { Integration } from './types/integration';
import { API, graphqlOperation } from 'aws-amplify';
import '../../settingsStyles.css';

const listIntegrationConfigsQuery = /* GraphQL */ `
  query ListIntegrationConfigs(
    $filter: ModelIntegrationConfigFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listIntegrationConfigs(
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        orgID
        crmName
        categoryID
        isActive
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

const Integrations = () => {
  const navigate = useNavigate();
  const [selectedIntegration, setSelectedIntegration] =
    useState<Integration | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [integrationsList, setIntegrationsList] =
    useState<Integration[]>(integrations);

  const refresh = async () => {
    try {
      const orgID = (window as any)?.me?.orgID;
      const result = (await API.graphql(
        graphqlOperation(listIntegrationConfigsQuery, {
          filter: {
            orgID: { eq: orgID },
          },
          limit: 1000,
        })
      )) as any;

      // Check for GraphQL errors (null items in list are common and non-critical)
      // If we have data, errors about null items can be safely ignored
      if (
        result?.errors &&
        Array.isArray(result.errors) &&
        result?.data?.listIntegrationConfigs
      ) {
        // Only log if there are errors AND no data (critical error)
        // If data exists, null item errors are non-critical and handled by filtering
        const hasData = result?.data?.listIntegrationConfigs?.items?.length > 0;
        if (!hasData) {
          console.warn('GraphQL returned errors and no data:', result.errors);
        }
        // Otherwise, silently handle - null items will be filtered out below
      }

      // Only proceed if we have data (errors about null items are non-critical)
      if (!result?.data?.listIntegrationConfigs) {
        console.error(
          'Error fetching integration status: No data returned',
          result
        );
        return;
      }

      const items = result?.data?.listIntegrationConfigs?.items || [];
      // Guard against nulls and filter out deleted items
      const allIntegrations = items.filter(
        (it: any) => it && it.isDeleted !== true
      );

      // Update integrations with connection status (by crmName matching integration.name, case-insensitive)
      const updatedIntegrations = integrations.map((integration) => {
        const found = allIntegrations.find(
          (it: any) =>
            String(it?.crmName || '').toLowerCase() ===
            integration.name.toLowerCase()
        );

        if (found) {
          // Integration exists - check if it's active or disabled
          const status: Integration['status'] =
            found.isActive === true ? 'connected' : 'disabled';
          return {
            ...integration,
            status,
            isConnected: found.isActive === true,
            configId: found?.id,
          };
        }

        // Integration not found - use default status
        return {
          ...integration,
          status: integration.status,
          isConnected: false,
          configId: undefined,
        };
      });

      setIntegrationsList(updatedIntegrations);
    } catch (error: any) {
      // Only log if it's a real error, not just GraphQL errors with data
      if (error?.data || error?.errors) {
        // GraphQL returned errors but might have data - check if we can still process it
        if (error?.data?.listIntegrationConfigs) {
          // We have data, so process it despite errors (null items are filtered out)
          const items = error.data.listIntegrationConfigs.items || [];
          const allIntegrations = items.filter(
            (it: any) => it && it.isDeleted !== true
          );

          const updatedIntegrations = integrations.map((integration) => {
            const found = allIntegrations.find(
              (it: any) =>
                String(it?.crmName || '').toLowerCase() ===
                integration.name.toLowerCase()
            );

            if (found) {
              const status: Integration['status'] =
                found.isActive === true ? 'connected' : 'disabled';
              return {
                ...integration,
                status,
                isConnected: found.isActive === true,
                configId: found?.id,
              };
            }

            return {
              ...integration,
              status: integration.status,
              isConnected: false,
              configId: undefined,
            };
          });

          setIntegrationsList(updatedIntegrations);
          // Don't log as error since we successfully processed the data
          return;
        }
      }
      // Real error - log it
      console.error('Error fetching integration status:', error);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleLearnMore = (integration: Integration) => {
    setSelectedIntegration(integration);
    setIsModalOpen(true);
  };

  const handleGetStarted = (integration: Integration) => {
    if (integration.setupUrl) {
      navigate(integration.setupUrl);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedIntegration(null);
  };

  return (
    <Box className="permissions-console-container" sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ mb: 6 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Integrations
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: '800px' }}>
          Connect your favorite tools and services to enhance your workflow and
          boost productivity
        </Typography>
      </Box>

      {/* Integrations Grid */}
      <Grid container spacing={3}>
        {integrationsList.map((integration) => (
          <Grid item xs={12} md={6} lg={4} key={integration.id}>
            <IntegrationCard
              integration={integration}
              onLearnMore={handleLearnMore}
              onGetStarted={handleGetStarted}
            />
          </Grid>
        ))}
      </Grid>

      {/* Modal */}
      {selectedIntegration && (
        <IntegrationModal
          integration={selectedIntegration}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </Box>
  );
};

export default Integrations;
